import {
  Accordion,
  Button,
  EButtonColorVariant,
  EHeadingStyleVariant,
  ELinkColorVariant,
  ELinkVariant,
  ETextStyleVariant,
  Heading,
  Link,
  Text,
} from '@outdoorsyco/bonfire';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ERentalType } from '@/services/analytics/types';

import styles from './BecomeAHost.module.css';
import { useHostingRVFlow } from './RV/useHostingRVFlow';
import { useHostingStayFlow } from './Stay/useHostingStayFlow';
import { useHostingType } from './useHostingType';

export const FAQs = () => {
  const { rentalType, isRentalCampground } = useHostingType();
  const intl = useIntl();
  const { redirectToHostingRVFlow, loading } = useHostingRVFlow();
  const { redirectToHostingStayFlow } = useHostingStayFlow();

  const handleHostingFlow = () => {
    if (rentalType === ERentalType.RV) {
      redirectToHostingRVFlow();
    } else if (rentalType === ERentalType.STAY) {
      redirectToHostingStayFlow();
    }
  };
  const content = {
    [ERentalType.RV]: {
      faqItems: [
        {
          question: intl.formatMessage({
            defaultMessage: 'How much does it cost to list my RV?',
            id: 'Qc5nzs',
          }),
          answer: intl.formatMessage({
            defaultMessage:
              "It's FREE to list on Outdoorsy. A small transaction fee is taken from each successful booking.",
            id: 'yPL0kM',
          }),
        },
        {
          question: intl.formatMessage({
            defaultMessage: 'How does Outdoorsy verify renters?',
            id: '2afsqA',
          }),
          answer: intl.formatMessage({
            defaultMessage:
              "All renters pass an extensive driving history check. We validate renter's driving history in every booking made on our platform.",
            id: 'gKZINA',
          }),
        },
        {
          question: intl.formatMessage({
            defaultMessage: 'Can I decide who can rent my RV?',
            id: 'N8n/BO',
          }),
          answer: intl.formatMessage({
            defaultMessage: 'Yes. You will always have the final say in who gets to rent your RV.',
            id: 'FXl+dY',
          }),
        },
        {
          question: intl.formatMessage({
            defaultMessage: 'How does the pick-up process work?',
            id: 'u9WHQr',
          }),
          answer: intl.formatMessage({
            defaultMessage:
              "You'll arrange to meet with renters on the day of pick up to exchange keys & give them a walkthrough of your RV.",
            id: '7Cjqar',
          }),
        },
        {
          question: intl.formatMessage({ defaultMessage: 'How do I get paid?', id: 'Ji9Fjy' }),
          answer: intl.formatMessage({
            defaultMessage:
              'Payments are deposited directly into your bank 24 - 48 hours after your RV has departed.',
            id: 'FVmGFU',
          }),
        },
        {
          question: intl.formatMessage({
            defaultMessage: 'What does insurance cover?',
            id: 'z/9mSV',
          }),
          answer: intl.formatMessage({
            defaultMessage:
              "Outdoorsy's $1 million liability insurance policy covers your RV or trailer whenever it’s being rented. You're also covered for theft and damage subject to our policies.",
            id: 'CJJmul',
          }),
        },
        {
          question: intl.formatMessage({ defaultMessage: 'Got more questions?', id: 'FzYXDd' }),
          answer: intl.formatMessage({
            defaultMessage: 'Check out our FAQ or give us a call at 1-877-723-7232.',
            id: 'Sc+vfg',
          }),
        },
      ],
    },
    [ERentalType.STAY]: {
      faqItems: [
        {
          question: intl.formatMessage({
            defaultMessage: 'Why should I list on Outdoorsy when I already list elsewhere?',
            id: 'V91QZ0',
          }),
          answer: (
            <Text variant={ETextStyleVariant.MediumRegular}>
              {intl.formatMessage({
                defaultMessage:
                  'It’s simple, listing on multiple sites helps keep your calendar full. Plus, if you’re part of our Stays launch, you’ll increase your take-home pay with reduced fees for 1 year.',
                id: '5WWjUH',
              })}
            </Text>
          ),
        },
        {
          question: intl.formatMessage({
            defaultMessage: 'How much can I expect to earn?',
            id: 'JEUgR3',
          }),
          answer: (
            <Text variant={ETextStyleVariant.MediumRegular}>
              {intl.formatMessage({
                defaultMessage:
                  'For your first year, if you’re part of the Stays launch, you’ll take home 96% of each rental, minus taxes. That means your hosting fee is just 4% — one of the lowest you’ll find on any platform. After your first year, you’ll pay a 10% fee on each rental.',
                id: '0FE0ou',
              })}{' '}
            </Text>
          ),
        },
        {
          question: intl.formatMessage({
            defaultMessage: 'What types of properties can I list?',
            id: 'vSdDaM',
          }),
          answer: (
            <Text variant={ETextStyleVariant.MediumRegular}>
              {intl.formatMessage({
                defaultMessage:
                  'Stays with a strong connection to beautiful terrain and outdoor activities, plus a high level of comfort for guests. Anything from a tent or a yurt to a cabin, pod, or treehouse.',
                id: 'IHQBWC',
              })}
            </Text>
          ),
        },
        {
          question: intl.formatMessage({
            defaultMessage: 'How does Outdoorsy support and protect me as a host?',
            id: 'Q1ELDt',
          }),
          answer: (
            <Text variant={ETextStyleVariant.MediumRegular}>
              {intl.formatMessage({
                defaultMessage:
                  'We provide 24 / 7 live support by phone, email, or chat, and we’re committed to your education through events like live webinars. You can also connect directly with other hosts through our community. As for protection, we cover every stay with industry-leading insurance coverage.',
                id: 'tXrQZf',
              })}
            </Text>
          ),
        },
        {
          question: intl.formatMessage({
            defaultMessage:
              'What integrations does Outdoorsy support to help me manage my stay bookings?',
            id: 'vtZSGo',
          }),
          answer: (
            <Text variant={ETextStyleVariant.MediumRegular}>
              {intl.formatMessage({
                defaultMessage:
                  'We currently support ICS calendar integrations and will be adding additional connections to property management systems such as Newbook, ResNexus, and more. This is just the start and our team is dedicated to partnering with our hosts to build best of breed, host-first solutions. Please feel free to contact us to share your ideas on how we can serve you better.',
                id: 'ZK+RbA',
              })}
            </Text>
          ),
        },
        {
          question: intl.formatMessage({
            defaultMessage: 'Can I list a stay in a state that is not live yet?',
            id: 'd2h0pc',
          }),
          answer: (
            <>
              <Text variant={ETextStyleVariant.MediumRegular} component={'span'}>
                {intl.formatMessage({
                  defaultMessage: 'You’ll soon be able to list stays across the U.S. Fill out',
                  id: '1W5Xf2',
                })}
              </Text>
              &nbsp;
              <Link
                colorVariant={ELinkColorVariant.Tertiary}
                href="https://go.outdoorsy.com/list-your-stay"
                label={intl.formatMessage({
                  defaultMessage: 'this form',
                  id: 'SmVZkw',
                })}
                variant={ELinkVariant.NoUnderline}
                target="_blank"
              />
              &nbsp;
              <Text variant={ETextStyleVariant.MediumRegular} component={'span'}>
                {intl.formatMessage({
                  defaultMessage: 'to get notified when we are live in your state!',
                  id: '8R6X+b',
                })}
              </Text>
            </>
          ),
        },
        {
          question: intl.formatMessage({
            defaultMessage: 'Can property management companies list on Outdoorsy?',
            id: 'RmqaYm',
          }),
          answer: (
            <>
              <Text variant={ETextStyleVariant.MediumRegular} component={'span'}>
                {intl.formatMessage({
                  defaultMessage:
                    'Yes, if you manage vacation rentals with a strong connection to the outdoors, you may be able to join our Stays launch. Tell us about your properties using',
                  id: '7Iuz6x',
                })}
              </Text>
              &nbsp;
              <Link
                colorVariant={ELinkColorVariant.Tertiary}
                href="https://go.outdoorsy.com/list-your-stay"
                label={intl.formatMessage({
                  defaultMessage: 'this form',
                  id: 'SmVZkw',
                })}
                variant={ELinkVariant.NoUnderline}
                target="_blank"
              />
              <Text variant={ETextStyleVariant.MediumRegular} component={'span'}>
                {intl.formatMessage({
                  defaultMessage: ', and we’ll be in touch.',
                  id: 'EKzY3r',
                })}
              </Text>
            </>
          ),
        },
      ],
    },
    [ERentalType.CAMPGROUND]: {
      faqItems: [
        {
          question: intl.formatMessage({
            defaultMessage: 'Is listing my campground free?',
            id: '26v9ir',
          }),
          answer: (
            <Text variant={ETextStyleVariant.MediumRegular}>
              {intl.formatMessage({
                defaultMessage:
                  'Yes, listing your campground on Outdoorsy is free. After that, hosts are charged a per booking fee based on the nightly rate for campsite bookings and stay bookings.',
                id: '7qoi75',
              })}
            </Text>
          ),
        },
        {
          question: intl.formatMessage({
            defaultMessage: 'Why does Outdoorsy have a campground page pre-made for me?',
            id: 'duw7k1',
          }),
          answer: (
            <Text variant={ETextStyleVariant.MediumRegular}>
              {intl.formatMessage({
                defaultMessage:
                  'Outdoorsy collects publicly available information online about all campgrounds across the US, to provide information to guests who are interested in booking at your campground.',
                id: 'JgtvBf',
              })}
            </Text>
          ),
        },
        {
          question: intl.formatMessage({
            defaultMessage: 'What property management systems integrate with Outdoorsy?',
            id: 'zZk+tT',
          }),
          answer: (
            <>
              <Text variant={ETextStyleVariant.MediumRegular} component={'span'}>
                {intl.formatMessage({
                  defaultMessage:
                    'Outdoorsy currently supports the Newbook property management system. We are working hard on many other integrations! Sign up on the',
                  id: 'owImKb',
                })}
              </Text>
              &nbsp;
              <Link
                colorVariant={ELinkColorVariant.Tertiary}
                href="#start-listing-form"
                label={intl.formatMessage({
                  defaultMessage: 'form',
                  id: 'dQAepC',
                })}
                variant={ELinkVariant.NoUnderline}
              />
              &nbsp;
              <Text variant={ETextStyleVariant.MediumRegular} component={'span'}>
                {intl.formatMessage({
                  defaultMessage: 'above to get more information.',
                  id: 'gW/Uxx',
                })}
              </Text>
            </>
          ),
        },
        {
          question: intl.formatMessage({
            defaultMessage: 'Why should I partner with Outdoorsy when I already have a website?',
            id: '2sij54',
          }),
          answer: (
            <Text variant={ETextStyleVariant.MediumRegular}>
              {intl.formatMessage({
                defaultMessage:
                  'Outdoorsy is the premier outdoor travel brand and will help you get more bookings on your campground! Work with us to reach more potential guests and lock in those bookings. Keep your existing website and booking platform. Outdoorsy seamlessly connects with your property management system, displaying accurate information, real-time availability, and pricing for your accommodations.',
                id: 'XHi45L',
              })}
            </Text>
          ),
        },
        {
          question: intl.formatMessage({
            defaultMessage: 'What if my campground bookings are by phone?',
            id: '38d2bT',
          }),
          answer: (
            <>
              <Text variant={ETextStyleVariant.MediumRegular} component={'span'}>
                {intl.formatMessage({
                  defaultMessage:
                    'Outdoorsy currently supports the Newbook property management system. We are working hard on many other integrations! Sign up on the',
                  id: 'owImKb',
                })}
              </Text>
              &nbsp;
              <Link
                colorVariant={ELinkColorVariant.Tertiary}
                href="#start-listing-form"
                label={intl.formatMessage({
                  defaultMessage: 'form',
                  id: 'dQAepC',
                })}
                variant={ELinkVariant.NoUnderline}
              />
              &nbsp;
              <Text variant={ETextStyleVariant.MediumRegular} component={'span'}>
                {intl.formatMessage({
                  defaultMessage: 'above to get more information.',
                  id: 'gW/Uxx',
                })}
              </Text>
            </>
          ),
        },
      ],
    },
  };

  return (
    <section id="faqs" className={`${styles.spacing} text-center bg-white`}>
      <Text variant={ETextStyleVariant.LegalRegular} className="uppercase">
        <FormattedMessage defaultMessage="FAQS" id="PsZKr3" />
      </Text>
      <Heading variant={EHeadingStyleVariant.H3} className="mt-2 mb-6">
        <FormattedMessage defaultMessage="What hosts are asking about" id="Dspv0l" />
      </Heading>

      <Accordion
        allowMultipleOpen
        className={`${!isRentalCampground ? 'mb-16' : ''} mt-12 text-left`}>
        {content[rentalType].faqItems.map(item => (
          <Accordion.Item divider title={item.question} key={item.question}>
            {item.answer}
          </Accordion.Item>
        ))}
      </Accordion>

      {!isRentalCampground && (
        <>
          <Heading variant={EHeadingStyleVariant.H4Alt} className="mb-8">
            <FormattedMessage defaultMessage="Ready to get started?" id="P+1fsm" />
          </Heading>
          <Button
            onClick={handleHostingFlow}
            variant={EButtonColorVariant.Primary}
            label={intl.formatMessage({ defaultMessage: 'Try hosting today', id: 'R7Xwt5' })}
            loading={loading}
          />
        </>
      )}
    </section>
  );
};
